import { toast, useLocalStorage } from '@buffer-mono/popcorn'
import usePortalContainer from '@buffer-mono/popcorn/src/helpers/usePortalContainer'
import * as React from 'react'
import { useEffect, useLayoutEffect } from 'react'
import type { JSX } from 'react'
import { createPortal } from 'react-dom'

import { useUser } from '../../common/context/User'
import { AccountType } from '../../common/events/types'
import useDismissBanner, {
  BannerTypes,
} from '../../common/hooks/useDismissBanner'
import { MODALS } from '../../common/types'
import type { Service } from '../../exports/Orchestrator/channelConnections/types'
import { getServiceName } from '../../exports/Orchestrator/channelConnections/utils'
import type { ObPlanId } from '../../gql/graphql'
import useChannels from '../Modal/hooks/useChannels'
import { ModalContent } from '../Modal/ModalContent'
import {
  cleanupInstagramPersonalAuthUrlParam,
  cleanupLinkedInCapabilitiesUrlParam,
  cleanupUrlParams,
  shouldPrefillComposerDataAfterConnection,
  shouldShowChannelConnectionPrompt,
  shouldShowDownloadAppModal,
  shouldShowTeamMemberDowngradeModal,
  shouldShowTrialExpiredModal,
  shouldShowTrialSignupWelcomeModal,
  shouldSkipCelebrationModals,
  shouldTriggerAnnualUpgradeFromUrl,
  shouldTriggerChannelOnboardingModalFromUrl,
  shouldTriggerChannelRefreshModalFromUrl,
  shouldTriggerInstagramPersonalAuthFromUrl,
  shouldTriggerLinkedInCapabilitiesFromUrl,
  shouldTriggerStartTrialFromUrl,
  shouldTriggerSuccessModalFromUrl,
} from '../Modal/utils'
import { UpdateType } from '../PlanSelector/types'
import { useModalManager } from './hooks/useModalManager'
import {
  handleAnnualUpgradeDeeplinkViaURL,
  handleChannelConnectionOnboardingViaURL,
  handleChannelRefreshViaURL,
  handleStartTrialIntentViaURL,
} from './ModalManagerUtils'

const ModalManager = (): JSX.Element => {
  const { openModal } = useModalManager()
  const [promoteDownloadAppModal] = useLocalStorage(
    'promoteDownloadAppModal',
    true,
  )

  const { isActive: isChannelGroupsPromptActive } = useDismissBanner({
    banner: BannerTypes.channelGroupsAnnouncement,
  })

  const shouldPromoteDownloadAppModal =
    promoteDownloadAppModal && shouldShowDownloadAppModal()

  const user = useUser()

  const { connectedChannels = [], connectedChannelIds = [] } = useChannels()

  const shouldShowChannelGroupsPrompt = connectedChannelIds.length >= 3
  const canStartTrial = user?.currentOrganization?.billing?.canStartTrial
  const organizationActions = user?.currentOrganization?.allowedActions

  useLayoutEffect(() => {
    if (!user?.id || !shouldTriggerStartTrialFromUrl()) return

    handleStartTrialIntentViaURL({ openModal, user })
  }, [organizationActions, canStartTrial, openModal, user])

  useLayoutEffect(() => {
    if (!user?.id || !shouldTriggerLinkedInCapabilitiesFromUrl()) return

    openModal({
      key: MODALS.linkedInCapabilities,
    })
    cleanupLinkedInCapabilitiesUrlParam()
  }, [openModal, user?.id])

  // Fallback for Instagram Login OAuth attempts the user declined (cancelled
  // the professional-account conversion or denied permissions): the publish
  // channel-authorization page redirects here with this param so we can offer
  // the personal (notification-based) account setup instead.
  useLayoutEffect(() => {
    if (!user?.id || !shouldTriggerInstagramPersonalAuthFromUrl()) return

    openModal({
      key: MODALS.instagramSelectAccountType,
      data: { accountType: AccountType.personal },
    })
    cleanupInstagramPersonalAuthUrlParam()
  }, [openModal, user?.id])

  useEffect(() => {
    if (!user?.id) return

    /* This modal must have the highest priority to be shown up to mobile users and avoid
     they see another modal which they cannot interact with */
    if (shouldPromoteDownloadAppModal) {
      openModal({
        key: MODALS.downloadApp,
      })
    }

    // Show Team Member Downgrade Modal
    if (shouldShowTeamMemberDowngradeModal(user)) {
      openModal({
        key: MODALS.teamMemberDowngrade,
      })
    }

    // Show Trial Expired Modal
    if (shouldShowTrialExpiredModal(user)) {
      openModal({
        key: MODALS.trialExpired,
        data: {
          ctaButton: 'noclick',
          upgradePathName: 'noclickTrialExpiredModal-upgrade',
          ctaView: 'trialExpired',
          ctaLocation: ' noclick',
        },
      })
    }

    // Composer channel-connection flows use these flags when the composer,
    // not posting-goal onboarding, must be the next focused surface.
    if (
      shouldTriggerChannelOnboardingModalFromUrl() &&
      !shouldSkipCelebrationModals() &&
      !shouldPrefillComposerDataAfterConnection()
    ) {
      handleChannelConnectionOnboardingViaURL({ openModal })
    }

    if (shouldTriggerChannelRefreshModalFromUrl()) {
      handleChannelRefreshViaURL({ openModal })
    }

    if (shouldTriggerAnnualUpgradeFromUrl()) {
      handleAnnualUpgradeDeeplinkViaURL({ openModal, user })
    }

    if (shouldTriggerSuccessModalFromUrl()) {
      // Always use the new modal for Stripe checkout success
      // This is a plan change/upgrade scenario that needs proper welcome messaging
      openModal({
        key: MODALS.subscriptionUpdateModern,
        data: {
          ctaButton: 'noclick',
          upgradePathName: 'noclickCheckoutSuccess-upgrade',
          ctaView: 'successModal',
          ctaLocation: ' noclickCheckoutSuccess',
          subscriptionUpdate: {
            updateType: UpdateType.SubscriptionUpgrade,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            planId: user.currentOrganization?.billing?.subscription?.plan
              ?.id as ObPlanId,
            scheduledUpdate: null,
          },
        },
      })
      cleanupUrlParams()
    }

    // Show Channel Connection prompt
    if (shouldShowChannelConnectionPrompt(user)) {
      openModal({
        key: MODALS.channelConnectionPrompt,
      })
    }

    // Show Trial Signup Welcome Modal
    if (shouldShowTrialSignupWelcomeModal(user)) {
      openModal({
        key: MODALS.trialSignupWelcomeModal,
      })
    }
  }, [user.currentOrganization?.id])

  useEffect(() => {
    if (shouldShowChannelGroupsPrompt && isChannelGroupsPromptActive) {
      openModal({
        key: MODALS.channelGroupsPrompt,
      })
    }
  }, [shouldShowChannelGroupsPrompt, isChannelGroupsPromptActive, openModal])

  useEffect(() => {
    // Open Composer with Prefilled Data
    if (
      connectedChannels &&
      connectedChannels.length > 0 &&
      shouldPrefillComposerDataAfterConnection()
    ) {
      if (window.__openComposer) {
        const cta = 'connectChannelOnboarding-previewMode'
        window.__openComposer(
          {
            channels: connectedChannelIds,
            cta: `publish-modal-${cta}-1`,
          },
          {
            prefillFromLocalStorage: true,
          },
        )
        if (shouldSkipCelebrationModals()) {
          const service = connectedChannels[0].service
          toast.success(
            `Great! your ${getServiceName(service as Service)} channel${
              connectedChannelIds.length > 1 ? 's have' : ' has'
            } been Connected 🎉`,
          )
        }
        cleanupUrlParams()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedChannels, window.__openComposer])

  const container = usePortalContainer()

  if (!user?.id) return <></>

  return createPortal(<ModalContent />, container ?? document.body)
}

const ModalManagerMemo = React.memo(ModalManager)

export { ModalManagerMemo as ModalManager }
export default ModalManagerMemo
